/**
 * AcumenOverlay — The Acumen-10 Ambient AI Layer
 * 
 * Two entry points:
 * 1. ⬡ button bottom-right — always visible, click to open
 * 2. Cmd+K / Ctrl+K — keyboard shortcut, works anywhere in the app
 * 
 * The overlay is pre-loaded with full session context before the user types.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAcumen } from '../../context/AcumenContext';
import { useTheme } from '../../context/ThemeContext';

const MODES = [
  { id: 'assist', label: 'Ask', icon: '⬡' },
  { id: 'score', label: 'Score', icon: '◎' },
  { id: 'draft', label: 'Draft', icon: '✦' },
  { id: 'analyze', label: 'Analyze', icon: '◈' },
];

function MarkdownResponse({ text }) {
  // Simple markdown-ish renderer for bold, bullets, headers
  const lines = text.split('\n');
  return (
    <div style={{ fontSize: 13, lineHeight: 1.65, color: 'inherit' }}>
      {lines.map((line, i) => {
        if (line.startsWith('## ')) return <h3 key={i} style={{ fontWeight: 700, fontSize: 13, marginTop: 10, marginBottom: 2 }}>{line.slice(3)}</h3>;
        if (line.startsWith('# ')) return <h2 key={i} style={{ fontWeight: 700, fontSize: 14, marginTop: 10, marginBottom: 4 }}>{line.slice(2)}</h2>;
        if (line.startsWith('- ') || line.startsWith('* ')) {
          return <div key={i} style={{ paddingLeft: 14, marginBottom: 2 }}>• {renderInline(line.slice(2))}</div>;
        }
        if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
        return <p key={i} style={{ margin: '2px 0' }}>{renderInline(line)}</p>;
      })}
    </div>
  );
}

function renderInline(text) {
  // Bold **text**
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

export default function AcumenOverlay() {
  const {
    isOpen, openPalette, closePalette,
    query, setQuery, response, loading, query_acumen, sessionContext,
  } = useAcumen();
  const { darkMode } = useTheme();

  const [mode, setMode] = useState('assist');
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) closePalette();
        else openPalette();
      }
      if (e.key === 'Escape' && isOpen) closePalette();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, openPalette, closePalette]);

  // Focus input when palette opens
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading) return;
    setSubmitted(true);
    await query_acumen(query.trim(), mode);
  };

  // Context pills — show what Acumen already knows
  const contextPills = [];
  if (sessionContext.solicitation_title) contextPills.push({ label: sessionContext.solicitation_title, color: '#3b82f6' });
  if (sessionContext.vendor_name) contextPills.push({ label: sessionContext.vendor_name, color: '#8b5cf6' });
  if (sessionContext.set_aside) contextPills.push({ label: sessionContext.set_aside, color: '#10b981' });
  if (sessionContext.page) contextPills.push({ label: sessionContext.page, color: '#f59e0b' });

  const bg = darkMode ? '#0f1117' : '#ffffff';
  const border = darkMode ? '#1e2435' : '#e5e7eb';
  const inputBg = darkMode ? '#1a1f2e' : '#f9fafb';
  const text = darkMode ? '#e2e8f0' : '#111827';
  const subtext = darkMode ? '#64748b' : '#6b7280';
  const accentColor = '#6366f1';

  return (
    <>
      {/* ── Persistent ⬡ Trigger Button ── */}
      <button
        onClick={() => isOpen ? closePalette() : openPalette()}
        title="Acumen-10 AI (Cmd+K)"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9998,
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: isOpen ? accentColor : (darkMode ? '#1e2435' : '#111827'),
          border: `2px solid ${isOpen ? accentColor : (darkMode ? '#2d3748' : '#374151')}`,
          color: '#fff',
          fontSize: 20,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          transition: 'all 0.2s ease',
        }}
      >
        ⬡
      </button>

      {/* ── Palette Backdrop ── */}
      {isOpen && (
        <div
          onClick={closePalette}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          }}
        />
      )}

      {/* ── Palette Panel ── */}
      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: '12%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            width: '100%',
            maxWidth: 680,
            borderRadius: 16,
            background: bg,
            border: `1px solid ${border}`,
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '14px 16px 10px',
            borderBottom: `1px solid ${border}`,
          }}>
            <span style={{ color: accentColor, fontSize: 18 }}>⬡</span>
            <span style={{ color: text, fontWeight: 700, fontSize: 14 }}>Acumen-10</span>
            <span style={{ color: subtext, fontSize: 12, marginLeft: 2 }}>— AI Acquisition Intelligence</span>
            <span style={{ marginLeft: 'auto', color: subtext, fontSize: 11, fontFamily: 'monospace', background: darkMode ? '#1a1f2e' : '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>⌘K</span>
          </div>

          {/* Context pills */}
          {contextPills.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 16px 4px' }}>
              <span style={{ fontSize: 11, color: subtext, marginRight: 2 }}>Context:</span>
              {contextPills.map((p, i) => (
                <span key={i} style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 20,
                  background: `${p.color}18`, color: p.color,
                  border: `1px solid ${p.color}40`,
                }}>
                  {p.label}
                </span>
              ))}
            </div>
          )}

          {/* Mode selector */}
          <div style={{ display: 'flex', gap: 4, padding: '8px 16px 0' }}>
            {MODES.map(m => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${mode === m.id ? accentColor : border}`,
                  background: mode === m.id ? `${accentColor}18` : 'transparent',
                  color: mode === m.id ? accentColor : subtext,
                  fontWeight: mode === m.id ? 600 : 400,
                  transition: 'all 0.15s',
                }}
              >
                {m.icon} {m.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} style={{ padding: '10px 16px 12px' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <textarea
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSubmitted(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
                placeholder={`Ask Acumen anything about this ${sessionContext.page || 'session'}... (Enter to send, Shift+Enter for newline)`}
                rows={2}
                style={{
                  flex: 1, resize: 'none', borderRadius: 10, padding: '10px 12px',
                  background: inputBg, border: `1px solid ${border}`,
                  color: text, fontSize: 14, outline: 'none', fontFamily: 'inherit',
                  lineHeight: 1.5,
                }}
              />
              <button
                type="submit"
                disabled={!query.trim() || loading}
                style={{
                  padding: '10px 16px', borderRadius: 10, border: 'none',
                  background: (!query.trim() || loading) ? (darkMode ? '#2d3748' : '#e5e7eb') : accentColor,
                  color: (!query.trim() || loading) ? subtext : '#fff',
                  cursor: (!query.trim() || loading) ? 'not-allowed' : 'pointer',
                  fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
              >
                {loading ? '...' : 'Send →'}
              </button>
            </div>
          </form>

          {/* Response area */}
          {(loading || (submitted && response)) && (
            <div style={{
              margin: '0 16px 16px',
              borderRadius: 10,
              background: darkMode ? '#1a1f2e' : '#f9fafb',
              border: `1px solid ${border}`,
              padding: '14px 16px',
              maxHeight: 360,
              overflowY: 'auto',
              color: text,
            }}>
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: subtext, fontSize: 13 }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: '50%',
                    border: `2px solid ${accentColor}40`, borderTopColor: accentColor,
                    animation: 'acumen-spin 0.7s linear infinite',
                  }} />
                  Acumen is analyzing your context...
                  <style>{`@keyframes acumen-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : (
                <MarkdownResponse text={response} />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
